import clsx from "clsx";
import * as React from "react";
import { Heading } from "../heading/Heading.js";
import { Text } from "../text/Text.js";

type CardColorProps =
  | "white"
  | "purple"
  | "blue"
  | "red"
  | "lightblue"
  | "indigo"
  | "yellow";
interface BaseCardProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: "vertical" | "horizontal";
  padding?: "sm" | "md" | "lg";
  title?: string;
  color?: CardColorProps;
  description?: string;
  classNames?: {
    image?: string;
    icon?: string;
    title?: string;
    description?: string;
    content?: string;
  };
}

interface ImageCardProps extends BaseCardProps {
  image?: string;
  icon?: never;
}

interface IconCardProps extends BaseCardProps {
  icon?: React.ReactNode;
  image?: never;
}

type CardProps = ImageCardProps | IconCardProps;

interface CardComponent
  extends React.ForwardRefExoticComponent<
    CardProps & React.RefAttributes<HTMLDivElement>
  > {
  Section: typeof CardSection;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>((props, ref) => {
  const {
    orientation = "vertical",
    title,
    description,
    children,
    className,
    classNames,
    color = "white",
    padding = "md",
    ...rest
  } = props;
  return (
    <div
      ref={ref}
      data-orientation={orientation}
      data-color={color}
      className={clsx("ox-reset ox-Card", className)}
      {...rest}
    >
      {props.image ? (
        <div className="ox-reset ox-CardImageWrapper">
          <img
            data-orientation={orientation}
            src={props.image}
            alt=""
            className={clsx("ox-reset ox-CardImage", classNames?.image)}
          />
        </div>
      ) : props.icon ? (
        <div className={clsx("ox-reset ox-CardIconWrapper", classNames?.icon)}>
          {props.icon}
        </div>
      ) : null}
      <div
        data-padding={padding}
        className={clsx("ox-CardContent", classNames?.content)}
        data-orientation={orientation}
      >
        {title ? (
          <Heading
            as="h3"
            fontWeight="bold"
            className={clsx("ox-reset ox-cardTitle", classNames?.title)}
          >
            {title}
          </Heading>
        ) : null}

        {description ? (
          <Text
            size="md"
            className={clsx(
              "ox-reset ox-CardDescription",
              classNames?.description,
            )}
          >
            {description}
          </Text>
        ) : null}

        <React.Fragment>{children}</React.Fragment>
      </div>
    </div>
  );
}) as CardComponent;

const CardSection = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const { children, className, ...rest } = props;
  return (
    <div
      ref={ref}
      className={clsx("ox-reset ox-CardSection", className)}
      {...rest}
    >
      {children}
    </div>
  );
});

Card.displayName = "Card";
CardSection.displayName = "CardSection";
Card.Section = CardSection;
export { Card };
export type { CardProps };
